import { useEffect, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";

let facebookSdkPromise;
let initializedFacebookAppId;

// O fluxo hospedado pela Meta pode incluir criação da conta e verificação do telefone.
const EMBEDDED_SIGNUP_TIMEOUT_MS = 10 * 60 * 1000;
const SIGNUP_DETAILS_TIMEOUT_MS = 2 * 60 * 1000;

const loadFacebookSdk = (appId, version) => {
    const initialize = (facebook) => {
        if (initializedFacebookAppId !== appId) {
            facebook.init({
                appId,
                autoLogAppEvents: true,
                xfbml: false,
                version
            });
            initializedFacebookAppId = appId;
        }
        return facebook;
    };

    if (window.FB) {
        return Promise.resolve(initialize(window.FB));
    }

    if (!facebookSdkPromise) {
        facebookSdkPromise = new Promise((resolve, reject) => {
            const previousCallback = window.fbAsyncInit;
            window.fbAsyncInit = () => {
                try {
                    previousCallback?.();
                } catch (error) {
                    reject(error);
                    return;
                }
                if (window.FB) {
                    resolve(window.FB);
                } else {
                    reject(new Error("Facebook SDK não foi inicializado."));
                }
            };

            let script = document.getElementById("facebook-jssdk");
            if (!script) {
                script = document.createElement("script");
                script.id = "facebook-jssdk";
                script.async = true;
                script.src = "https://connect.facebook.net/en_US/sdk.js";
                script.onerror = () => reject(new Error("Não foi possível carregar o Facebook SDK."));
                document.head.appendChild(script);
            }
        }).then((facebook) => {
            facebookSdkPromise = null;
            return facebook;
        }, (error) => {
            facebookSdkPromise = null;
            throw error;
        });
    }

    return facebookSdkPromise.then(initialize);
};

const waitForEmbeddedSignup = (facebook, configId, t) => new Promise((resolve, reject) => {
    let authorizationCode;
    let signupDetails;
    let settled = false;
    let codeTimeout;
    const signupTimeout = setTimeout(
        () => finish(new Error(t("whatsapp_embedded_signup_timeout", { minutes: EMBEDDED_SIGNUP_TIMEOUT_MS / 60000 }))),
        EMBEDDED_SIGNUP_TIMEOUT_MS
    );

    const cleanup = () => {
        window.removeEventListener("message", handleMessage);
        clearTimeout(signupTimeout);
        clearTimeout(codeTimeout);
    };

    const finish = (error, result) => {
        if (settled) {
            return;
        }
        settled = true;
        cleanup();
        if (error) {
            reject(error);
        } else {
            resolve(result);
        }
    };

    const completeWhenReady = () => {
        if (authorizationCode && signupDetails) {
            finish(null, { authorizationCode, ...signupDetails });
        }
    };

    const handleMessage = (event) => {
        let hostname;
        try {
            hostname = new URL(event.origin).hostname.toLowerCase();
        } catch (error) {
            return;
        }
        if (!/(^|\.)facebook\.com$/.test(hostname)) {
            return;
        }

        let payload = event.data;
        if (typeof payload === "string") {
            try {
                payload = JSON.parse(payload);
            } catch (error) {
                return;
            }
        }
        if (!payload || payload.type !== "WA_EMBEDDED_SIGNUP") {
            return;
        }
        if (payload.event === "CANCEL") {
            finish(new Error(t("whatsapp_embedded_signup_cancelled")));
            return;
        }
        if (payload.event === "ERROR") {
            finish(new Error(payload.data?.error_message || t("whatsapp_embedded_signup_failed")));
            return;
        }
        const completedEvents = new Set([
            "FINISH",
            "FINISH_ONLY_WABA",
            "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING",
            "FINISH_OBO_MIGRATION",
            "FINISH_GRANT_ONLY_API_ACCESS"
        ]);
        if (!completedEvents.has(payload.event)) {
            return;
        }

        const phoneNumberId = payload.data?.phone_number_id
            || payload.data?.business_phone_number_id
            || "";
        const businessAccountId = payload.data?.waba_id
            || payload.data?.business_account_id
            || (Array.isArray(payload.data?.waba_ids) && payload.data.waba_ids.length === 1
                ? payload.data.waba_ids[0] : "");
        if (!/^\d+$/.test(String(businessAccountId || ""))) {
            finish(new Error(t("whatsapp_embedded_signup_missing_ids")));
            return;
        }
        // Alguns tipos de conclusão (por exemplo, WABA-only/coexistência) não enviam o ID do telefone.
        // O backend pode descobri-lo pela WABA quando ela contém um único número.
        if (phoneNumberId && !/^\d+$/.test(String(phoneNumberId))) {
            finish(new Error(t("whatsapp_embedded_signup_missing_ids")));
            return;
        }
        signupDetails = { phoneNumberId, businessAccountId };
        completeWhenReady();
    };

    window.addEventListener("message", handleMessage);
    try {
        facebook.login((response) => {
            authorizationCode = response?.authResponse?.code;
            if (!authorizationCode) {
                finish(new Error(t("whatsapp_embedded_signup_cancelled")));
                return;
            }
            codeTimeout = setTimeout(
                () => finish(new Error(t("whatsapp_embedded_signup_details_timeout"))),
                SIGNUP_DETAILS_TIMEOUT_MS
            );
            completeWhenReady();
        }, {
            config_id: configId,
            response_type: "code",
            override_default_response_type: true,
            extras: { setup: {} }
        });
    } catch (error) {
        finish(error);
    }
});

const WhatsappEmbeddedSignupButton = ({ metaSetupInfo, onConnected, onResult }) => {
    const { t } = useTranslation();
    const [loadingSdk, setLoadingSdk] = useState(false);
    const [facebookReady, setFacebookReady] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sdkError, setSdkError] = useState("");

    useEffect(() => {
        let mounted = true;
        if (!metaSetupInfo?.embeddedSignupReady) {
            setFacebookReady(false);
            setLoadingSdk(false);
            return undefined;
        }

        setLoadingSdk(true);
        setSdkError("");
        loadFacebookSdk(metaSetupInfo.embeddedSignupAppId, metaSetupInfo.graphApiVersion)
            .then(() => {
                if (mounted) {
                    setFacebookReady(true);
                }
            })
            .catch((error) => {
                if (mounted) {
                    setSdkError(error.message || t("whatsapp_embedded_signup_failed"));
                }
            })
            .finally(() => {
                if (mounted) {
                    setLoadingSdk(false);
                }
            });

        return () => {
            mounted = false;
        };
    }, [metaSetupInfo, t]);

    const handleConnect = async () => {
        setSubmitting(true);
        onResult?.(null);
        try {
            const signup = await waitForEmbeddedSignup(
                window.FB,
                metaSetupInfo.embeddedSignupConfigId,
                t
            );
            const response = await axiosInstance.post(Api.parameter.whatsapp_embedded_signup, {
                code: signup.authorizationCode,
                phoneNumberId: signup.phoneNumberId,
                businessAccountId: signup.businessAccountId
            });
            const data = response.data || {};
            const complete = Boolean(data.activationComplete);
            onResult?.({
                variant: complete ? "success" : "warning",
                message: data.message || t("whatsapp_embedded_signup_partial")
            });
            await onConnected?.(data);
        } catch (error) {
            onResult?.({
                variant: "danger",
                message: error?.response?.data?.message
                    || error.message
                    || t("whatsapp_embedded_signup_failed")
            });
        } finally {
            setSubmitting(false);
        }
    };

    const setupReady = Boolean(metaSetupInfo?.embeddedSignupReady);
    return (
        <div className="whatsapp-embedded-signup">
            {sdkError && <Alert variant="danger" className="mb-3">{sdkError}</Alert>}
            <Button
                type="button"
                variant="success"
                className="d-flex align-items-center gap-2"
                onClick={handleConnect}
                disabled={!setupReady || !facebookReady || submitting || loadingSdk}
            >
                {loadingSdk || submitting
                    ? <><Spinner animation="border" size="sm" /> {t("whatsapp_embedded_signup_connecting")}</>
                    : <><i className="fa-brands fa-whatsapp" aria-hidden="true"></i> {t("whatsapp_embedded_signup_connect")}</>}
            </Button>
        </div>
    );
};

export default WhatsappEmbeddedSignupButton;
